import { includes, map, without } from "lodash";
import { useGrowl } from "PFApp/use_growl";
import { ActionIcon } from "PFComponents/action_icon";
import SectionBox from "PFComponents/box/section_box";
import { Button } from "PFComponents/button";
import Checkbox from "PFComponents/checkbox/checkbox";
import { LoadingDots } from "PFComponents/loading_dots";
import Modal from "PFComponents/modal/modal";
import { Typography } from "PFComponents/typography";
import { useLocalisationExport } from "PFCore/hooks/queries/admin/localisation/use_localisation_export";
import { Id, LocalisationKeys } from "PFTypes";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./localisation_export.module.scss";

type ExportModalProps = {
  accountId?: Id;
  allowedLocales: string[];
  localisationKey: LocalisationKeys;
  onClose?: () => void;
};

export const ExportModal = ({ accountId, allowedLocales, localisationKey, onClose }: ExportModalProps) => {
  const { t } = useTranslation(["admin", "translation"]);
  const { t: locales } = useTranslation("locales");

  const growl = useGrowl();

  const [selectedLocales, selectLocales] = useState<string[]>(allowedLocales);
  const [isExporting, setIsExporting] = useState(false);

  const { refetch } = useLocalisationExport(accountId || 0, localisationKey, selectedLocales, {
    enabled: false,
    onSuccess: (data) => {
      setIsExporting(false);
      // Bypass CORS limitation
      window.open(data, "_blank");
      onClose?.();
    },
    onError: () => {
      growl({
        message: t("admin:localisation.exportModal.exportFailed"),
        kind: "error"
      });

      setIsExporting(false);
    }
  });

  const handleSelectLocale = (locale: string, value: boolean) =>
    selectLocales(value ? [...selectedLocales, locale] : without(selectedLocales, locale));

  const handleExport = () => {
    setIsExporting(true);
    refetch();
  };

  return (
    //@ts-ignore-line
    <Modal closeOnBackdropClick handleClose={onClose} classes={{ dialog: css.exportModal }}>
      <SectionBox className={css.modal}>
        <section className={css.header}>
          <Typography variant="h4" noMargin>
            {t(`admin:localisation.exportModal.header.${localisationKey}`)}
          </Typography>
          <ActionIcon name="cross" size="sm" onClick={onClose} />
        </section>
        <Typography variant="bodyRegular" noMargin>
          {t(`admin:localisation.exportModal.description.${localisationKey}`)}
        </Typography>
        {map(allowedLocales, (locale) => (
          <Checkbox
            checked={includes(selectedLocales, locale)}
            label={locales(locale as unknown as TemplateStringsArray)}
            handleChange={(value) => handleSelectLocale(locale, value)}
          />
        ))}
        <section className={css.footer}>
          <Button kind="tertiary" text={t("translation:cancel")} onClick={onClose} />
          <Button disabled={isExporting || !selectedLocales.length} onClick={handleExport}>
            {isExporting ? <LoadingDots /> : t("admin:localisation.exportModal.confirmExport")}
          </Button>
        </section>
      </SectionBox>
    </Modal>
  );
};
